import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- только api, без barrel (иначе в alty тянется PromocodeCodeGrid → @adv-frontend/grid)
import { invalidateCachePromocodesCount } from '#entities/promocode/api/api';
import {
    COLLECTION_GLOBAL_TAG,
    PROMO_RECOMMENDATIONS_GLOBAL_TAG,
    PROMOS_LIST_GLOBAL_TAG,
} from '#shared/api/globalTags';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiActivateDiscountPathParameters,
    type OpenApiActivateDiscountResponseSuccess,
    type OpenApiActivateDiscountSearchParams,
    type OpenApiCreateDiscountRequest,
    type OpenApiCreateDiscountResponseSuccess,
    type OpenApiDeleteDiscountPathParameters,
    type OpenApiDeleteDiscountResponseSuccess,
    type OpenApiGetDiscountFiltersRequest,
    type OpenApiGetDiscountFiltersResponseSuccess,
    type OpenApiGetDiscountPathParameters,
    type OpenApiGetDiscountResponseSuccess,
    type OpenApiGetDiscountsResponseSuccess,
    type OpenApiGetDiscountsSearchParams,
    type OpenApiSchemaDiscount,
    type OpenApiUpdateDiscountPathParameters,
    type OpenApiUpdateDiscountRequest,
    type OpenApiUpdateDiscountResponseSuccess,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';
import {
    removePromoFromPromosCache,
    updatePromoActivationInPromosCache,
    updatePromoIsCurrentlyActiveInPromosCache,
} from './promosApi';

const DiscountTag = 'Discount' as const;

const discountGridTag = { type: DiscountTag, id: 'discountGrid' };
const discountFiltersTag = { type: DiscountTag, id: 'discountFilters' };

const getPromoRecommendationsInvalidationTags = (discount: Pick<OpenApiSchemaDiscount, 'promo_offer_id'>) =>
    discount.promo_offer_id ? [PROMO_RECOMMENDATIONS_GLOBAL_TAG] : [];

const getCollectionInvalidationTags = (
    discount: Pick<OpenApiSchemaDiscount, 'collection_id'>,
    payload: OpenApiUpdateDiscountRequest
) => {
    if (discount.collection_id) {
        return [{ type: COLLECTION_GLOBAL_TAG, id: discount.collection_id }];
    }

    // PATCH-схема допускает ответ без collection_id, хотя поля коллекции уже изменились.
    if (payload.collection_title !== undefined || payload.collection_description !== undefined) {
        return [COLLECTION_GLOBAL_TAG];
    }

    return [];
};

export const discountGridTagInvalidator = new InvalidateGridApiHelper<
    typeof DiscountTag,
    OpenApiGetDiscountsSearchParams
>(DiscountTag, 'discountGrid');

const getDiscountTag = ({ id }: Pick<OpenApiSchemaDiscount, 'id'>) => {
    return { type: DiscountTag, id };
};

function updateDiscountInGridCache(
    data: OpenApiGetDiscountsSearchParams,
    onDiscountUpdate: (currentVariant: OpenApiSchemaDiscount) => OpenApiSchemaDiscount | null
) {
    discountApi.endpoints.getList.updateQueryData(data, (draft) => {
        if (draft) {
            const newDiscounts: OpenApiSchemaDiscount[] = [];

            draft.discounts.forEach((discount) => {
                const newDiscount = onDiscountUpdate(discount);

                if (newDiscount) {
                    newDiscounts.push(newDiscount);
                }
            });
            draft.discounts = newDiscounts;
        }
    });
}

export const discountApi = createApi({
    name: 'entities/discount/api/api',
    tagTypes: [DiscountTag, COLLECTION_GLOBAL_TAG, PROMOS_LIST_GLOBAL_TAG, PROMO_RECOMMENDATIONS_GLOBAL_TAG],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getList: builder.query<OpenApiGetDiscountsResponseSuccess, OpenApiGetDiscountsSearchParams>({
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [discountGridTag, ...discountGridTagInvalidator.provideHashTagForGridEndpoint(args)];
                },
                query: (params) => {
                    return {
                        url: `/discounts`,
                        params,
                        method: 'GET',
                    };
                },
            }),
            getById: builder.query<OpenApiGetDiscountResponseSuccess, OpenApiGetDiscountPathParameters>({
                query: ({ id }) => {
                    return { url: `/discounts/${id}`, method: 'GET' };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        // общий тэг для инвалидации всех getById запросов
                        { type: DiscountTag, id: 'getDiscount' },
                        getDiscountTag(result),
                    ];
                },
            }),
            create: builder.mutation<OpenApiCreateDiscountResponseSuccess, OpenApiCreateDiscountRequest>({
                query: (discount) => {
                    return {
                        url: '/discounts',
                        method: 'POST',
                        body: discount,
                    };
                },
                invalidatesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        discountGridTag,
                        discountFiltersTag,
                        PROMOS_LIST_GLOBAL_TAG,
                        ...getPromoRecommendationsInvalidationTags(result),
                    ];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    invalidateCachePromocodesCount();
                },
            }),
            update: builder.mutation<
                OpenApiUpdateDiscountResponseSuccess,
                { params: OpenApiUpdateDiscountPathParameters; payload: OpenApiUpdateDiscountRequest }
            >({
                query: ({ params: { id }, payload }) => {
                    return {
                        url: `/discounts/${id}`,
                        method: 'PATCH',
                        body: payload,
                    };
                },
                invalidatesTags: (result, error, request) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        discountGridTag,
                        getDiscountTag({ id: request.params.id }),
                        discountFiltersTag,
                        ...getCollectionInvalidationTags(result, request.payload),
                        PROMOS_LIST_GLOBAL_TAG,
                    ];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: updatedDiscount } = queryFulfilledResult;

                    updatePromoIsCurrentlyActiveInPromosCache(updatedDiscount.id, updatedDiscount.is_currently_active);
                },
            }),
            changeDeactivated: builder.mutation<
                OpenApiActivateDiscountResponseSuccess,
                { params: OpenApiActivateDiscountPathParameters; payload: OpenApiActivateDiscountSearchParams }
            >({
                query: ({ params: { id }, payload }) => {
                    return {
                        url: `/discounts/${id}/activated`,
                        params: payload,
                        method: 'POST',
                    };
                },
                invalidatesTags: (_, error, { params: { id } }) => {
                    if (error) {
                        return [];
                    }

                    return [
                        ...discountGridTagInvalidator.getTagsToInvalidate(),
                        getDiscountTag({ id }),
                        PROMOS_LIST_GLOBAL_TAG,
                    ];
                },
                async onQueryStarted({ params: { id }, payload }, { queryFulfilled }) {
                    const is_active = payload.is_active;

                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: newDiscount } = queryFulfilledResult;

                    discountApi.endpoints.getById.patchQueryData({ id }, [
                        { op: 'replace', path: ['is_active'], value: is_active },
                    ]);

                    if (discountGridTagInvalidator.lastArgs) {
                        updateDiscountInGridCache(discountGridTagInvalidator.lastArgs, (draft) => {
                            if (draft.id === id) {
                                return newDiscount;
                            }

                            return draft;
                        });
                    }

                    updatePromoActivationInPromosCache(id, {
                        is_currently_active: newDiscount.is_currently_active,
                        status: newDiscount.is_active ? 'ACTIVE' : 'INACTIVE',
                    });
                },
            }),
            delete: builder.mutation<
                OpenApiDeleteDiscountResponseSuccess,
                OpenApiDeleteDiscountPathParameters & Pick<OpenApiSchemaDiscount, 'promo_offer_id'>
            >({
                query: ({ id }) => {
                    return {
                        url: `/discounts/${id}`,
                        method: 'DELETE',
                    };
                },
                invalidatesTags: (_, error, { id, promo_offer_id }) => {
                    if (error) {
                        return [];
                    }

                    const promoRecommendationTags = getPromoRecommendationsInvalidationTags({ promo_offer_id });

                    // Если lastArgs null, значит, что Grid прямо сейчас не виден в DOM, инвалидируем его целиком
                    if (!discountGridTagInvalidator.lastArgs) {
                        return [
                            discountGridTag,
                            getDiscountTag({ id }),
                            PROMOS_LIST_GLOBAL_TAG,
                            ...promoRecommendationTags,
                        ];
                    }

                    return [
                        ...discountGridTagInvalidator.getTagsToInvalidate(),
                        getDiscountTag({ id }),
                        discountFiltersTag,
                        PROMOS_LIST_GLOBAL_TAG,
                        ...promoRecommendationTags,
                    ];
                },
                async onQueryStarted({ id }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    invalidateCachePromocodesCount();

                    if (discountGridTagInvalidator.lastArgs) {
                        updateDiscountInGridCache(discountGridTagInvalidator.lastArgs, (draft) => {
                            if (draft.id === id) {
                                return null;
                            }

                            return draft;
                        });
                    }

                    removePromoFromPromosCache(id);
                },
            }),
            getListForFilters: builder.query<
                OpenApiGetDiscountFiltersResponseSuccess,
                OpenApiGetDiscountFiltersRequest
            >({
                query: () => {
                    return {
                        url: `/discounts/filters`,
                        method: 'GET',
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [discountFiltersTag];
                },
            }),
        };
    },
});

export const useGetDiscountById = discountApi.useLazyGetByIdQuery;
export const createDiscount = discountApi.endpoints.create.mutate;
export const updateDiscount = discountApi.endpoints.update.mutate;
export const changeDiscountDeactivated = discountApi.endpoints.changeDeactivated.mutate;
export const useDeleteDiscount = discountApi.useDeleteMutation;
export const useGetDiscountsForFilters = discountApi.useGetListForFiltersQuery;
